import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useFormChangeHandler } from "hooks/useFormChangeHandler";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";
import Select from "react-select"; // Importing react-select for searchable dropdown
import { getRequest, postRequest } from "services/api";

const DAY_OFF_OPTIONS = [
    { value: "MINGGU", label: "MINGGU" },
    { value: "SENIN", label: "SENIN" },
    { value: "SELASA", label: "SELASA" },
    { value: "RABU", label: "RABU" },
    { value: "KAMIS", label: "KAMIS" },
    { value: "JUMAT", label: "JUMAT" },
    { value: "SABTU", label: "SABTU" },
];

const EMPLOYMENT_STATUS_OPTIONS = [
    { value: "active", label: "ACTIVE" },
    { value: "inactive", label: "INACTIVE" },
    { value: "resigned", label: "RESIGNED" },
];

const normalizeDayOffValue = (value) => {
    const text = String(value || "").trim().toLowerCase();
    if (!text) return "";
    const aliases = {
        sunday: "MINGGU",
        minggu: "MINGGU",
        monday: "SENIN",
        senin: "SENIN",
        tuesday: "SELASA",
        selasa: "SELASA",
        wednesday: "RABU",
        rabu: "RABU",
        thursday: "KAMIS",
        kamis: "KAMIS",
        friday: "JUMAT",
        jumat: "JUMAT",
        "jum'at": "JUMAT",
        saturday: "SABTU",
        sabtu: "SABTU",
    };
    return aliases[text] || String(value || "").trim().toUpperCase();
};

const CreatePage = ({ initialData, onSuccess, onError, autoRole }) => {
    const API_URL = "/user";

    // State untuk menyimpan form data. Jika initialData tersedia, isi form untuk update
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
        department_id: null,
        position_id: null,
        day_off: "",
        nomor_rekening: "",
        staff_note: "",
        employment_status: "active",
        join_date: "",
        birth_date: "",
    });

    // State untuk departments dan positions
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);

    const isUpdateMode = !!initialData; // Cek apakah ini update mode atau create mode

    // API hook untuk create dan update
    const { createData: createUser } = useCreateData(
        API_URL,
        "users"
    );
    const { updateData: updateUser } = useUpdateData(
        `${API_URL}/${initialData?.id}`,
        "users"
    );

    // Gunakan hook untuk menangani perubahan form
    const handleChange = useFormChangeHandler(setFormData);

    // Custom submit function to handle auto role sync
    const submitFunction = async (payload) => {
        const action = isUpdateMode ? updateUser : createUser;
        const result = await action(payload);
        
        // Auto assign role via attach endpoint jika ini create mode dan sukses
        if (result.success && autoRole && !isUpdateMode) {
            try {
                // response dari useCreateData mengembalikan { success: true, data: API_RESPONSE }
                // dimana API_RESPONSE adalah { success: true, data: { id: ... } }
                const userId = result.data?.data?.id || result.data?.id;

                if (userId) {
                    await postRequest("/user-role/attach", {
                        user_id: userId,
                        role: autoRole
                    });
                } else {
                    console.error("User ID not found in response:", result);
                }
            } catch (err) {
                console.error("Failed to auto attach user role:", err);
            }
        }
        return result;
    };

    // Use hook for handling form submission
    const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
        submitFunction,
        onSuccess,
        onError
    );

    const handleBeforeSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            day_off: normalizeDayOffValue(formData.day_off),
            employment_status: formData.employment_status || "active",
        };
        
        if (autoRole && !isUpdateMode) {
            payload.role = autoRole;
        }

        handleSubmit(payload, e);
    };

    // Fetch departments and positions data
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await getRequest("/ahr/departments?all=1");
                if (response.success) {
                    setDepartments(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch departments:", error);
            }
        };

        const fetchPositions = async () => {
            try {
                const response = await getRequest("/ahr/positions?all=1");
                if (response.success) {
                    setPositions(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch positions:", error);
            }
        };

        fetchDepartments();
        fetchPositions();
    }, []);

    useEffect(() => {
        // If initialData is provided (for update), fill the form with existing data
        if (initialData) {
            setFormData({
                ...initialData,
                employment_status: initialData.employment_status || initialData.employee_status || initialData.status || "active",
            }); // Adjust based on the structure of initialData
        }
    }, [initialData]);

    return (
        <form
            onSubmit={(e) => handleBeforeSubmit(e)}
            className="d-flex flex-column"
            style={{ height: "100%" }}
        >
            <div className="action-sheet-content flex-grow-1">
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="invoice_number"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Name :
                    </label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx(
                                "form-control gotra-form-control rounded-1",
                                formError?.name && "is-invalid"
                            )}
                            id="name"
                            placeholder="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px", height: "35px" }}
                        />
                        {formError?.name && (
                            <div className="invalid-feedback">{formError.name}</div>
                        )}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="invoice_number"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Email :
                    </label>
                    <div className="col-sm-8">
                        <input
                            type="email"
                            className={clsx(
                                "form-control gotra-form-control rounded-1",
                                formError?.email && "is-invalid"
                            )}
                            id="email"
                            placeholder="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px", height: "35px" }}
                        />
                        {formError?.email && (
                            <div className="invalid-feedback">{formError.email}</div>
                        )}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="invoice_number"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Phone :
                    </label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx(
                                "form-control gotra-form-control rounded-1",
                                formError?.phone && "is-invalid"
                            )}
                            id="phone"
                            placeholder="Phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px", height: "35px" }}
                        />
                        {formError?.phone && (
                            <div className="invalid-feedback">{formError.phone}</div>
                        )}
                    </div>
                </div>
                {/* password */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="password"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Password :
                    </label>
                    <div className="col-sm-8">
                        <input
                            type="password"
                            className={clsx(
                                "form-control gotra-form-control rounded-1",
                                formError?.password && "is-invalid"
                            )}
                            id="password"
                            placeholder="Password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px", height: "35px" }}
                        />
                        {formError?.password && (
                            <div className="invalid-feedback">{formError.password}</div>
                        )}
                    </div>
                </div>

                {/* department */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="department_id"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Department :
                    </label>
                    <div className="col-sm-8">
                        <Select
                            id="department_id"
                            name="department_id"
                            value={departments.find(dept => dept.id === formData.department_id) ?
                                { value: formData.department_id, label: departments.find(dept => dept.id === formData.department_id).name } :
                                null}
                            onChange={(selectedOption) => {
                                setFormData(prev => ({
                                    ...prev,
                                    department_id: selectedOption ? selectedOption.value : null
                                }));
                            }}
                            options={departments.map(dept => ({
                                value: dept.id,
                                label: dept.name
                            }))}
                            placeholder="Select Department"
                            isClearable
                            isDisabled={isSubmitting}
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    fontSize: "14px",
                                    minHeight: "35px"
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                })
                            }}
                        />
                        {formError?.department_id && (
                            <div className="invalid-feedback d-block">{formError.department_id}</div>
                        )}
                    </div>
                </div>

                {/* position */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="position_id"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Position :
                    </label>
                    <div className="col-sm-8">
                        <Select
                            id="position_id"
                            name="position_id"
                            value={positions.find(pos => pos.id === formData.position_id) ?
                                { value: formData.position_id, label: positions.find(pos => pos.id === formData.position_id).name } :
                                null}
                            onChange={(selectedOption) => {
                                setFormData(prev => ({
                                    ...prev,
                                    position_id: selectedOption ? selectedOption.value : null
                                }));
                            }}
                            options={positions.map(pos => ({
                                value: pos.id,
                                label: pos.name
                            }))}
                            placeholder="Select Position"
                            isClearable
                            isDisabled={isSubmitting}
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    fontSize: "14px",
                                    minHeight: "35px"
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                })
                            }}
                        />
                        {formError?.position_id && (
                            <div className="invalid-feedback d-block">{formError.position_id}</div>
                        )}
                    </div>
                </div>

                {/* employment_status */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="employment_status"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Status :
                    </label>
                    <div className="col-sm-8">
                        <Select
                            inputId="employment_status"
                            classNamePrefix="react-select"
                            options={EMPLOYMENT_STATUS_OPTIONS}
                            value={EMPLOYMENT_STATUS_OPTIONS.find((option) => option.value === (formData.employment_status || "active")) || EMPLOYMENT_STATUS_OPTIONS[0]}
                            onChange={(option) => setFormData((previous) => ({ ...previous, employment_status: option?.value || "active" }))}
                            isDisabled={isSubmitting}
                            placeholder="Pilih status"
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: "35px",
                                    height: "35px",
                                    borderRadius: "4px",
                                    borderColor: formError?.employment_status ? "#d63939" : provided.borderColor,
                                    fontSize: "14px"
                                }),
                                valueContainer: (provided) => ({
                                    ...provided,
                                    height: "35px",
                                    padding: "0 8px",
                                    fontSize: "14px"
                                }),
                                indicatorsContainer: (provided) => ({
                                    ...provided,
                                    height: "35px"
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                })
                            }}
                        />
                        {formError?.employment_status && (
                            <div className="invalid-feedback d-block">{formError.employment_status}</div>
                        )}
                    </div>
                </div>

                {/* join_date */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="join_date"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Join Date :
                    </label>
                    <div className="col-sm-8">
                        <input
                            type="date"
                            className={`form-control ${formError?.join_date ? "is-invalid" : ""}`}
                            id="join_date"
                            name="join_date"
                            value={formData.join_date ? formData.join_date.split('T')[0] : ""}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px", height: "35px" }}
                        />
                        {formError?.join_date && (
                            <div className="invalid-feedback">{formError.join_date}</div>
                        )}
                    </div>
                </div>

                {/* birth_date */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="birth_date"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Birth Date :
                    </label>
                    <div className="col-sm-8">
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="date"
                                className={`form-control ${formError?.birth_date ? "is-invalid" : ""}`}
                                id="birth_date"
                                name="birth_date"
                                value={formData.birth_date ? formData.birth_date.split('T')[0] : ""}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                style={{ fontSize: "14px", height: "35px" }}
                            />
                            {formData.birth_date && (
                                <span className="text-muted text-nowrap" style={{ fontSize: "14px" }}>
                                    (Umur: {Math.floor((new Date() - new Date(formData.birth_date).getTime()) / 3.15576e+10)} tahun)
                                </span>
                            )}
                        </div>
                        {formError?.birth_date && (
                            <div className="invalid-feedback d-block">{formError.birth_date}</div>
                        )}
                    </div>
                </div>

                {/* day_off */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="day_off"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Day Off :
                    </label>
                    <div className="col-sm-8">
                        <Select
                            inputId="day_off"
                            classNamePrefix="react-select"
                            options={DAY_OFF_OPTIONS}
                            value={DAY_OFF_OPTIONS.find((option) => option.value === normalizeDayOffValue(formData.day_off)) || null}
                            onChange={(option) => setFormData((previous) => ({ ...previous, day_off: option?.value || "" }))}
                            isClearable
                            isDisabled={isSubmitting}
                            placeholder="Pilih day off"
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    minHeight: "35px",
                                    height: "35px",
                                    borderRadius: "4px",
                                    borderColor: formError?.day_off ? "#d63939" : provided.borderColor,
                                    fontSize: "14px"
                                }),
                                valueContainer: (provided) => ({
                                    ...provided,
                                    height: "35px",
                                    padding: "0 8px",
                                    fontSize: "14px"
                                }),
                                indicatorsContainer: (provided) => ({
                                    ...provided,
                                    height: "35px"
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                singleValue: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    fontSize: "14px"
                                })
                            }}
                        />
                        {formError?.day_off && (
                            <div className="invalid-feedback d-block">{formError.day_off}</div>
                        )}
                    </div>
                </div>

                {/* nomor_rekening */}
                <div className="row mb-3 align-items-center">
                    <label
                        htmlFor="nomor_rekening"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Account Number :
                    </label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx(
                                "form-control gotra-form-control rounded-1",
                                formError?.nomor_rekening && "is-invalid"
                            )}
                            id="nomor_rekening"
                            placeholder="Nomor Rekening"
                            name="nomor_rekening"
                            maxLength={50}
                            value={formData.nomor_rekening || ""}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px", height: "35px" }}
                        />
                        {formError?.nomor_rekening && (
                            <div className="invalid-feedback">{formError.nomor_rekening}</div>
                        )}
                    </div>
                </div>

                {/* staff_note */}
                <div className="row mb-3 align-items-start">
                    <label
                        htmlFor="staff_note"
                        className="col-sm-4 col-form-label d-flex justify-content-end"
                        style={{ fontSize: "14px" }}
                    >
                        Staff Note :
                    </label>
                    <div className="col-sm-8">
                        <textarea
                            className={clsx(
                                "form-control gotra-form-control rounded-1",
                                formError?.staff_note && "is-invalid"
                            )}
                            id="staff_note"
                            placeholder="Catatan pelanggaran / evaluasi staff"
                            name="staff_note"
                            rows={4}
                            value={formData.staff_note || ""}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: "14px" }}
                        />
                        {formError?.staff_note && (
                            <div className="invalid-feedback">{formError.staff_note}</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="form-group mt-auto d-grid">
                <button
                    type="submit"
                    className="btn btn-primary btn-block"
                    disabled={isSubmitting}
                >
                    {isUpdateMode ? "Update User" : "Create User"}
                </button>
            </div>
        </form>
    );
};

export default CreatePage;
